/**
 * Per-cell FactSet TTF (Text-to-Formula) dispatcher used by the Excel
 * AI cell-write loop in `excel/excelService.ts`. Sits between the
 * planner-emitted `CellModification.ttfQuery` and the host write:
 *
 *  1. Gate check — only attempts TTF when the per-run context says
 *     the user has FactSet OAuth + the TTF scope and the run hasn't
 *     been sticky-disabled by a prior `TtfReauthRequiredError`.
 *  2. L1 cache lookup — repeats of the same query string within an
 *     Excel session skip the upstream round trip entirely.
 *  3. Bounded TTF call — single submit + poll, hard 20s ceiling via
 *     `AbortController`. Anything slower silently falls back so the
 *     user never sees the call.
 *  4. Adapter — coerces the upstream JSON (which is owned by FactSet
 *     and typed as `unknown`) into the typed `TtfApiResponse` shape
 *     the host's `InsertTtfApiResponse` intent expects. Adapter null
 *     → fall back.
 *  5. Inline + host write — extracts the validated `=FDS(...)`
 *     string from `apiResponse.results[0]`, substitutes any
 *     `{Ticker}` / `{Input}` placeholders with their literal
 *     values, and writes the resulting single-cell formula via
 *     the same `setCellContents` (→ `insertContentsToRange`)
 *     path the LLM-fallback formulas use. The host's table-writer
 *     `InsertTtfApiResponse` intent is deliberately NOT used in
 *     per-cell mode — see the `TtfDispatchDeps.setCellContents`
 *     JSDoc for the (long) reason why.
 *
 * Outcomes:
 *  - `"dispatched"` — the host wrote the formula. The caller should
 *    SKIP its existing `applyCellModificationViaIntents` write for
 *    this cell, and treat the address as edited.
 *  - `"fall_through"` — for any other reason (gate closed, no
 *    `ttfQuery`, TTF failure, timeout, adapter null). The caller
 *    proceeds with its existing write so the supplied `formula`
 *    field still lands in the cell.
 *
 * Sticky disable: when the upstream returns `TtfReauthRequiredError`,
 * we mutate `args.runCtx.stickyDisabled = true` so subsequent cells in
 * the same run skip TTF entirely (every TTF call would hit the same
 * 401, wasting time). The fallback formulas keep landing as normal.
 *
 * Every dependency is injectable for testability — the helper imports
 * the production wiring as defaults so the cell-write loop calls it
 * with a single argument in production.
 */

import { setCellContents as defaultSetCellContents } from "../../excelHostOps";
import { diag } from "../../aiDiag";
import { Semaphore } from "../../../shared/utils/semaphore";
import {
  normalizeTtfQueryAbsoluteDates,
  runTtfQuery as defaultRunTtfQuery,
  TtfCancelledError,
  TtfFailedError,
  TtfQueryDatePolicyError,
  TtfReauthRequiredError,
  TtfTimeoutError,
} from "./ttfApi";
import { adaptTtfApiResponse as defaultAdaptTtfApiResponse } from "./ttfResponseAdapter";
import { excelTtfCache as defaultCache, TtfCache } from "./ttfCache";
import { forceReconnectFactSetOAuth } from "../../../shared/factset/factsetReconnect";
import type {
  TtfApiResponse,
  TtfApiResponseResult,
} from "./spreadsheetIntents";
import {
  EXCEL_TTF_PER_CALL_TIMEOUT_MS,
  TTF_MAX_CONCURRENT_QUERIES,
} from "../../../shared/factset/factsetConstants";
import { findFqlDatePolicyIssue } from "../../fqlDatePolicy";
import {
  inferFactSetFormatHint,
  normalizeFactSetNumberFormat,
  type FactSetFormatHint,
} from "../../factsetValueSemantics";

/**
 * Per-AI-run state threaded into every cell dispatch. `ttfAvailable`
 * is computed once at the top of `applyWorkbookModifications` from the
 * user's FactSet OAuth grant; `stickyDisabled` flips to `true` if any
 * cell in the run hits a reauth-required upstream error so we stop
 * burning round trips against an invalid token.
 */
export interface TtfRunContext {
  ttfAvailable: boolean;
  stickyDisabled: boolean;
  attemptsRemaining?: number;
  budgetRemainingMs?: number;
  /** Absolute run deadline so parallel/queued work shares one wall-clock budget. */
  deadlineAt?: number;
  diagnostics?: {
    connected?: boolean;
    hasTextToFormulaAccess?: boolean;
    requiredScope?: string;
    scopes?: string[];
    probeError?: string;
  };
}

/** Outcome of attempting TTF dispatch for a single cell. */
export interface TtfDispatchOutcome {
  outcome: "dispatched" | "fall_through";
  /**
   * Short tag describing why we fell through. Stable strings that are
   * safe to use in telemetry / log lines. Unset when `outcome ===
   * "dispatched"`.
   */
  reason?:
    | "gate_closed"
    | "sticky_disabled"
    | "no_query"
    | "cache_hit_dispatch_failed"
    | "reauth_required"
    | "timeout"
    | "cancelled"
    | "budget_exhausted"
    | "upstream_failed"
    | "adapter_failed"
    | "invalid_query_date"
    | "invalid_fql_date"
    | "host_write_failed"
    | "unknown_error";
  /**
   * The fully-inlined `=FDS(...)` formula the dispatcher wrote to
   * the cell. Set only when `outcome === "dispatched"` so the
   * caller's activity log can render the same `📐 A1 = =FDS(...)`
   * line shape it uses for LLM-emitted formulas. Helps debugging
   * ("did TTF actually pick a different formula than the LLM?")
   * without having to dig into devtools logs.
   */
  dispatchedFormula?: string;
  /** Number format written with the dispatched formula, when one was needed. */
  dispatchedNumberFormat?: string;
}

/** Replaceable wiring for the dispatcher. Every entry has a production default. */
export interface TtfDispatchDeps {
  runTtfQuery?: typeof defaultRunTtfQuery;
  /**
   * Per-cell formula writer. The dispatcher uses this to land the
   * inlined TTF formula on the target address. Defaults to the
   * production `setCellContents` from `excelHostOps`.
   *
   * Why this and not `insertTtfApiResponse` — the host's
   * `factset.Spreadsheet.InsertTtfApiResponse` intent is documented
   * (`f2_docs/excel/excel-tools.md` §InsertTtfApiResponse) as
   * composing `getActiveCellAddress` + `getRange` +
   * `insertContentsToRange` to render a multi-row, multi-column
   * **table** anchored at the active cell (Identifier label row,
   * ticker row, input value rows, formula description label, then
   * the actual `=FDS(...)` cell). That layout is fundamentally
   * mismatched to the per-cell `CellModification` contract the
   * planner emits — looping it once per cell stamps a fresh table
   * on each address and the next iteration overwrites it, leaving
   * "Identifier / AAPL-US / Sales" labels in the grid instead of
   * formulas (observed 2026-05-24).
   *
   * Per-cell mode therefore bypasses the table-writer intent and
   * writes the *inlined* formula string (see `inlineTtfFormula`)
   * directly via `setCellContents`. The richer TTF table surface
   * stays available for a future "TTF as a planner-callable tool"
   * surface where the agent explicitly asks for a grid.
   */
  setCellContents?: typeof defaultSetCellContents;
  cache?: Pick<TtfCache, "get" | "set">;
  adaptResponse?: typeof defaultAdaptTtfApiResponse;
  perCallTimeoutMs?: number;
  /** Optional structured logger. Defaults to `console.log` with a stable prefix. */
  logEvent?: (event: string, fields?: Record<string, unknown>) => void;
  /**
   * Optional activity-log hook. When provided, the dispatcher emits
   * a user-visible line (e.g. "TTF unavailable for cell A1 — used
   * supplied formula instead. (oauth)") every time it falls back
   * for a real failure. Gate-closed / no-query / cancelled outcomes
   * are intentionally quiet since they're expected steady-state.
   * The host caller wires this to the chat activity log so the user
   * can see that TTF was attempted and degraded gracefully.
   */
  onFallback?: (entry: TtfFallbackActivityEntry) => void;
}

/**
 * Coarse category for a TTF fall-through. Used both for the
 * `console.warn` line and for the `onFallback` activity-log entry.
 * Stable strings so external tooling can match on them.
 */
export type TtfFallbackCategory =
  | "oauth"
  | "rate-limit"
  | "parse"
  | "host"
  | "host-write"
  | "timeout";

/**
 * Shape passed to the activity-log hook. Includes the categorised
 * reason in addition to the raw dispatcher tag so the caller can
 * choose how to render it (e.g. "TTF unavailable for cell A1
 * (oauth)" vs. "TTF unavailable for cell A1 (upstream_failed)").
 */
export interface TtfFallbackActivityEntry {
  address: string;
  category: TtfFallbackCategory;
  reason: NonNullable<TtfDispatchOutcome["reason"]>;
  message?: string;
}

/**
 * In-process TTF hit-rate counters. Reset on page reload — used as a
 * quick dev-tools sanity check that TTF is actually hitting (not for
 * durable telemetry). Exposed via `getTtfDispatchStats()`.
 */
const ttfStats = {
  attempted: 0,
  dispatched: 0,
  fallbackByCategory: {
    oauth: 0,
    "rate-limit": 0,
    parse: 0,
    host: 0,
    "host-write": 0,
    timeout: 0,
  } as Record<TtfFallbackCategory, number>,
};

export function getTtfDispatchStats(): {
  attempted: number;
  dispatched: number;
  hitRate: number;
  fallbackByCategory: Record<TtfFallbackCategory, number>;
} {
  return {
    attempted: ttfStats.attempted,
    dispatched: ttfStats.dispatched,
    hitRate:
      ttfStats.attempted > 0
        ? ttfStats.dispatched / ttfStats.attempted
        : 0,
    fallbackByCategory: { ...ttfStats.fallbackByCategory },
  };
}

/**
 * Map a dispatcher fall-through reason to a coarse category for
 * external surfaces (console.warn, activity log). Returns `null` for
 * expected steady-state outcomes (gate closed, no query, sticky
 * disabled, cancelled) so the warn surface stays quiet.
 */
function categorizeFallThrough(
  reason: NonNullable<TtfDispatchOutcome["reason"]>,
): TtfFallbackCategory | null {
  switch (reason) {
    case "gate_closed":
    case "sticky_disabled":
    case "no_query":
    case "cancelled":
    case "budget_exhausted":
      return null;
    case "reauth_required":
      return "oauth";
    case "adapter_failed":
    case "invalid_query_date":
    case "invalid_fql_date":
      return "parse";
    case "timeout":
      return "timeout";
    case "host_write_failed":
    case "cache_hit_dispatch_failed":
      return "host-write";
    case "upstream_failed":
      return "host";
    case "unknown_error":
    default:
      return "host";
  }
}

interface TtfDispatchArgs {
  ttfQuery: string | undefined;
  address: string;
  /** Optional number format requested by the workbook modification. */
  numberFormat?: string;
  /** Do not infer a format when this content is overwritten later in-burst. */
  suppressInferredNumberFormat?: boolean;
  /**
   * Planner-emitted target sheet for this cell. Threaded through to
   * `setCellContents.sheetName` so the formula lands on the right
   * sheet even when the host's active sheet drifts mid-burst (e.g.
   * the user clicks into another tab while the apply is in flight).
   * The underlying `insertContentsToRange` wrapper builds the wire
   * `destinationCell` from `sheetName!address` so the host targets
   * unambiguously without an `ActivateWorksheet` round-trip.
   */
  sheetName?: string;
  /**
   * Workbook to pin this TTF write to, threaded through to
   * `setCellContents.workbookName`. Same routing story as
   * `sheetName` — the underlying `insertContentsToRange` builds an
   * external-ref `destinationCell` (`[Book1]Sheet1!A1`) so the host
   * targets the right workbook regardless of which one is currently
   * foreground.
   */
  workbookName?: string;
  runCtx: TtfRunContext;
  signal?: AbortSignal;
}

/**
 * `console.log`-backed default logger. Prefixed for grep-ability and
 * narrow enough that it survives the production console without
 * drowning out other diagnostics.
 */
function defaultLogEvent(event: string, fields: Record<string, unknown> = {}): void {
  try {
    console.log(`[FACTSET_TTF] ${event}`, fields);
  } catch {
    // Logging must never throw — best-effort.
  }
}

/**
 * Returns true when `error` looks like an `AbortSignal.abort()`-fired
 * error. Used to translate raw aborts into `TtfTimeoutError` from the
 * caller's POV (we hold the abort timer here).
 */
function isAbortError(error: unknown): boolean {
  if (typeof DOMException !== "undefined" && error instanceof DOMException && error.name === "AbortError") {
    return true;
  }
  return error instanceof Error && error.name === "AbortError";
}

function responseHasExplicitInputAnchors(response: TtfApiResponse): boolean {
  return response.results.some((result) => {
    const inputs = result.fql_conversion?.inputs;
    return inputs !== undefined && Object.keys(inputs).length > 0;
  });
}

/**
 * Build a fully self-contained FDS-family formula from a TTF API
 * result that can be dropped into a single cell with no external
 * cell references.
 *
 * The TTF API returns formulas in three observed shapes:
 *
 *  1. **Cell-reference form** (designed for the table layout):
 *     `=FDS(A1, "FF_SALES(QTR_R,"&A2&",,,,USD)") Where A1=AAPL-US, A2=0Q`
 *     The `InsertTtfApiResponse` table writer places the ticker in
 *     row 0, inputs in rows 1..N, and the formula in a later row so
 *     the cell references resolve. In per-cell mode this produces
 *     circular refs (`A1` references itself) and `#NUM!` errors.
 *
 *  2. **Placeholder form**: `=FDS({Ticker}, "FF_SALES({Period},...)")`
 *     The `{Ticker}` / `{InputKey}` tokens can be substituted from
 *     `result.ticker` and `result.fql_conversion.inputs`.
 *
 *  3. **Pre-inlined form**: `=FDS("AAPL-US","FF_SALES(QTR,0QY,,,USD)")`
 *     Already self-contained — no work needed.
 *
 * Strategy (in priority order):
 *
 *  A. **Reconstruct from `fql_with_inputs`** (handles cases 1 and 2):
 *     Take `fql_conversion.fql_with_inputs`, substitute every
 *     `{InputKey}` with `inputs[key].value`, then rebuild the returned
 *     FDS-family shell with a quoted ticker and the inlined FQL. This avoids
 *     the cell-reference form without converting FDSC/FDSB/etc. to FDS.
 *
 *  B. **Inline placeholders in `formula`** (fallback for case 2 when
 *     `fql_with_inputs` is absent): substitute `{Ticker}` and
 *     `{InputKey}` directly in the raw formula string.
 *
 *  C. **Pass through `formula` as-is** (case 3): already
 *     self-contained.
 *
 * Returns `null` when:
 *   - No formula and no `fql_with_inputs` are available.
 *   - More than one ticker (per-cell mode handles a single ticker).
 *   - Any `{...}` placeholder is left unsubstituted.
 *   - The formula still contains a trailing ` Where ...` annotation.
 */
/**
 * Parse the `Where A1=AAPL-US, A2=0Q` annotation the TTF API appends
 * to cell-reference formulas. Returns the cell-ref → value map, or
 * `null` when no `Where` clause is found.
 */
function parseWhereClause(
  rawFormula: string
): { formulaPart: string; refs: Record<string, string> } | null {
  const whereMatch = rawFormula.match(/^(.+)\s+Where\s+(.+)$/i);
  if (!whereMatch) return null;
  const formulaPart = whereMatch[1];
  const refs: Record<string, string> = {};
  for (const pair of whereMatch[2].split(/,\s*/)) {
    const eqIdx = pair.indexOf("=");
    if (eqIdx === -1) continue;
    const key = pair.slice(0, eqIdx).trim();
    const value = pair.slice(eqIdx + 1).trim();
    if (key && value) refs[key] = value;
  }
  return { formulaPart, refs };
}

interface FactSetFormulaShell {
  functionName: string;
  leadingArgs: string[];
}

/**
 * Capture the host-function shell around the identifier and FQL arguments.
 *
 * TTF can return FDSC/FDSB/etc. formulas whose leading arguments carry
 * behavior that must not be lost when `fql_with_inputs` is available. The FQL
 * expression itself is an Excel string literal, so commas and parentheses
 * inside it are ignored while splitting the outer function arguments.
 */
function parseFactSetFormulaShell(rawFormula: unknown): FactSetFormulaShell | null {
  if (typeof rawFormula !== "string") return null;
  const formulaPart = rawFormula.replace(/\s+Where\s+.+$/i, "").trim();
  const prefix = formulaPart.match(
    /^=\s*(FDS|FDSB|FDSC|FDSRC|FDSZ|FDT)\s*\(/i,
  );
  if (!prefix) return null;

  const args: string[] = [];
  let current = "";
  let nestedDepth = 0;
  let inString = false;

  for (let index = prefix[0].length; index < formulaPart.length; index += 1) {
    const char = formulaPart[index];
    if (char === '"') {
      current += char;
      if (inString && formulaPart[index + 1] === '"') {
        current += formulaPart[index + 1];
        index += 1;
      } else {
        inString = !inString;
      }
      continue;
    }
    if (!inString && char === "(") {
      nestedDepth += 1;
      current += char;
      continue;
    }
    if (!inString && char === ")") {
      if (nestedDepth > 0) {
        nestedDepth -= 1;
        current += char;
        continue;
      }
      args.push(current.trim());
      break;
    }
    if (!inString && nestedDepth === 0 && char === ",") {
      args.push(current.trim());
      current = "";
      continue;
    }
    current += char;
  }

  if (inString || args.length < 2) return null;
  return {
    functionName: prefix[1].toUpperCase(),
    leadingArgs: args.slice(0, -2),
  };
}

function escapeExcelString(value: string): string {
  return value.replace(/"/g, '""');
}

/**
 * Rebuild a structured TTF result while preserving its returned FDS-family
 * shell and any leading arguments (for example FDSC's direction argument).
 */
function buildFactSetFormulaFromFql(
  rawFormula: unknown,
  tickerExpression: string,
  fql: string,
): string {
  const shell = parseFactSetFormulaShell(rawFormula);
  const functionName = shell?.functionName ?? "FDS";
  const args = [
    ...(shell?.leadingArgs ?? []),
    tickerExpression,
    `"${escapeExcelString(fql)}"`,
  ];
  return `=${functionName}(${args.join(",")})`;
}

/**
 * Convert the first TTF API result into a single-cell `=FDS(...)` formula and
 * return the metadata needed to expose a reusable template to the LLM.
 *
 * Resolution is intentionally deterministic and ordered by how structured the
 * API response is:
 *   - Strategy A uses `fql_conversion.fql_with_inputs` and `inputs` when the
 *     API supplies that explicit structured template.
 *   - Strategy W uses the API's trailing `Where A1=value, A2=value` annotation
 *     when `fql_conversion` is absent. This preserves the API-provided cell-ref
 *     substitution map so callers can build `formula_template`/`placeholders`
 *     without asking the LLM to infer them.
 *   - Strategy B inlines `{Ticker}` / `{InputName}` placeholders from the
 *     available ticker and input values.
 *   - Strategy C passes through formulas that are already self-contained.
 *
 * @param result - A single adapted TTF API result containing the raw formula,
 *   optional ticker list, optional `fql_conversion`, and validation metadata.
 * @returns An `InlineResult` with the ready-to-write formula and strategy
 *   metadata, or `null` when the response cannot be safely represented as one
 *   single-cell formula.
 */
function inlineTtfFormula(result: TtfApiResponseResult): InlineResult | null {
  const tickers = Array.isArray(result.ticker) ? result.ticker : [];
  if (tickers.length > 1) {
    return null;
  }
  const ticker = tickers[0];
  const inputs = result.fql_conversion?.inputs;
  const fqlWithInputs = result.fql_conversion?.fql_with_inputs;

  // ── Strategy A: reconstruct from fql_with_inputs ──────────────
  if (
    typeof fqlWithInputs === "string" &&
    fqlWithInputs.length > 0 &&
    typeof ticker === "string" &&
    ticker.length > 0
  ) {
    let fql = fqlWithInputs;
    if (inputs) {
      for (const [key, input] of Object.entries(inputs)) {
        if (typeof input?.value !== "string") {
          continue;
        }
        fql = fql.split(`{${key}}`).join(input.value);
      }
    }
    if (/\{[^{}]+\}/.test(fql)) {
      diag("tool", "inline_formula", { strategy: "A", guardTripped: true, fqlWithInputs, fql, ticker });
      return null;
    }
    const formula = buildFactSetFormulaFromFql(
      result.formula,
      `"${escapeExcelString(ticker)}"`,
      fql,
    );
    diag("tool", "inline_formula", {
      strategy: "A",
      fqlWithInputs,
      inputsMap: inputs,
      ticker,
      postFormula: formula,
    });
    return { formula, strategy: "A" };
  }

  if (typeof result.formula !== "string" || result.formula.length === 0) {
    return null;
  }

  // ── Strategy W: parse the Where annotation to resolve cell refs ─
  const parsed = parseWhereClause(result.formula);
  if (parsed && Object.keys(parsed.refs).length > 0) {
    let formula = parsed.formulaPart;
    const sortedRefs = Object.entries(parsed.refs).sort(
      (a, b) => b[0].length - a[0].length
    );
    for (const [cellRef, value] of sortedRefs) {
      formula = formula.split(`"&${cellRef}&"`).join(value);
    }
    for (const [cellRef, value] of sortedRefs) {
      const escaped = cellRef.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const refPattern = new RegExp(`\\b${escaped}\\b`, "g");
      formula = formula.replace(refPattern, `"${value}"`);
    }
    if (/\b[A-Z]{1,3}\d{1,7}\b/.test(formula.replace(/"[^"]*"/g, ""))) {
      diag("tool", "inline_formula", { strategy: "W", guardTripped: true, preFormula: result.formula, postFormula: formula, whereRefs: parsed.refs });
      return null;
    }
    diag("tool", "inline_formula", { strategy: "W", preFormula: result.formula, postFormula: formula, whereRefs: parsed.refs });
    return { formula, strategy: "W", whereRefs: parsed.refs, formulaPart: parsed.formulaPart };
  }

  // ── Strategy B / C: inline {Placeholder} or pass through ──────
  let formula = result.formula.replace(/\s+Where\s+.+$/i, "");

  if (ticker) {
    formula = formula.split("{Ticker}").join(`"${ticker}"`);
  }
  if (inputs) {
    for (const [key, input] of Object.entries(inputs)) {
      if (typeof input?.value !== "string") {
        continue;
      }
      formula = formula.split(`{${key}}`).join(input.value);
    }
  }
  if (/\{[^{}]+\}/.test(formula)) {
    diag("tool", "inline_formula", { strategy: "B", guardTripped: true, preFormula: result.formula, postFormula: formula });
    return null;
  }
  const strategy = formula === result.formula ? "C" as const : "B" as const;
  diag("tool", "inline_formula", { strategy, preFormula: result.formula, postFormula: formula, ticker });
  return { formula, strategy };
}

/**
 * Place the TTF formula on the target cell.
 *
 * Per-cell mode rationale (2026-05-24): the host's
 * `factset.Spreadsheet.InsertTtfApiResponse` intent is a *table*
 * writer, not a single-cell formula writer — see the long
 * `TtfDispatchDeps.setCellContents` JSDoc above for the wire-shape
 * details. Looping it once per cell produces a garbled "Identifier
 * / AAPL-US / Sales" layout where each iteration overwrites the
 * previous table.
 *
 * Instead, we extract the validated `=FDS(...)` string from
 * `apiResponse.results[0]`, inline any `{Ticker}` / `{Input}`
 * placeholders, and write the result via the same per-cell
 * `setCellContents` path the LLM-fallback formulas use. The
 * formula is what makes the TTF detour worth it — the
 * Identifier/Description labels were just metadata for the table
 * layout we don't use here.
 *
 * Returns `null` when the response can't be coerced to a single
 * inlined formula (no formula, multiple tickers, unresolved
 * placeholder); the caller falls through to the LLM-emitted
 * formula on the same `CellModification` so the cell still lands.
 */
async function placeApiResponseAtCell(
  apiResponse: TtfApiResponse,
  args: TtfDispatchArgs,
  deps: TtfDispatchDeps
): Promise<
  | { ok: true; formula: string; numberFormat?: string }
  | { ok: false; reason: "adapter_failed" | "invalid_fql_date"; message?: string }
> {
  const setCellContents = deps.setCellContents ?? defaultSetCellContents;
  const firstResult = apiResponse.results[0];
  if (!firstResult) {
    return { ok: false, reason: "adapter_failed" };
  }
  const inlineResult = inlineTtfFormula(firstResult);
  if (!inlineResult) {
    return { ok: false, reason: "adapter_failed" };
  }
  const datePolicyIssue = findFqlDatePolicyIssue(inlineResult.formula);
  if (datePolicyIssue) {
    return {
      ok: false,
      reason: "invalid_fql_date",
      message: datePolicyIssue.message,
    };
  }
  const formatHint = inferFactSetFormatHint({
    formula: inlineResult.formula,
    formulaDetails: firstResult.formula_details,
  });
  const numberFormat =
    typeof args.numberFormat === "string" && args.numberFormat.length > 0
      ? normalizeFactSetNumberFormat(
          args.numberFormat,
          formatHint.semantics,
        ).format
      : args.suppressInferredNumberFormat
        ? undefined
        : formatHint.recommendedNumberFormat;
  await setCellContents(args.address, inlineResult.formula, {
    ...(args.sheetName ? { sheetName: args.sheetName } : {}),
    ...(args.workbookName ? { workbookName: args.workbookName } : {}),
    ...(numberFormat ? { numberFormat } : {}),
    undoEntryText: "FactSet AI: set cell (TTF)",
  });
  return {
    ok: true,
    formula: inlineResult.formula,
    ...(numberFormat ? { numberFormat } : {}),
  };
}

/**
 * Attempt to satisfy a single cell's `ttfQuery` via the FactSet TTF
 * API + the host's `InsertTtfApiResponse` intent. Falls back silently
 * for any failure mode so the supplied `formula` on the same
 * `CellModification` still lands.
 *
 * Mutates `args.runCtx.stickyDisabled = true` on
 * `TtfReauthRequiredError`.
 */
export async function tryDispatchCellViaTtf(
  args: TtfDispatchArgs,
  deps: TtfDispatchDeps = {}
): Promise<TtfDispatchOutcome> {
  const log = deps.logEvent ?? defaultLogEvent;

  // Every fall-through path below funnels through this so (a) the
  // in-process hit-rate counters stay accurate, (b) real failures
  // get a `console.warn` with the coarse category, and (c) the
  // optional activity-log hook fires with the same context.
  // Returning the outcome directly keeps every call site one-liner.
  const fallThrough = (
    reason: NonNullable<TtfDispatchOutcome["reason"]>,
    extra?: { message?: string; httpStatus?: number; serverErrorCode?: string },
  ): TtfDispatchOutcome => {
    const category = categorizeFallThrough(reason);
    if (category) {
      ttfStats.fallbackByCategory[category] += 1;
      try {
        console.warn(
          `[FACTSET_TTF] cell ${args.address} fell back — category=${category} reason=${reason}` +
            (extra?.message ? ` message="${extra.message}"` : "") +
            (extra?.httpStatus !== undefined ? ` httpStatus=${extra.httpStatus}` : "") +
            (extra?.serverErrorCode ? ` code=${extra.serverErrorCode}` : ""),
        );
      } catch {
        // Logging must never throw — best-effort.
      }
      if (deps.onFallback) {
        try {
          deps.onFallback({
            address: args.address,
            category,
            reason,
            message: extra?.message,
          });
        } catch {
          // Activity-log hook is best-effort.
        }
      }
    }
    return { outcome: "fall_through", reason };
  };

  if (!args.runCtx.ttfAvailable) {
    return fallThrough("gate_closed");
  }
  if (args.runCtx.stickyDisabled) {
    log("cell_skipped", { address: args.address, reason: "sticky_disabled" });
    return fallThrough("sticky_disabled");
  }
  const query = args.ttfQuery?.trim();
  if (!query) {
    return fallThrough("no_query");
  }
  if (args.signal?.aborted) {
    return fallThrough("cancelled");
  }

  const cache = deps.cache ?? defaultCache;
  const cached = cache.get(query);
  if (cached) {
    log("cache_hit", { address: args.address });
    try {
      if (args.signal?.aborted) {
        return fallThrough("cancelled");
      }
      ttfStats.attempted += 1;
      const placed = await placeApiResponseAtCell(cached, args, deps);
      if (placed.ok === false) {
        log("cell_fell_back", {
          address: args.address,
          reason: placed.reason,
          stage: "cache_hit_inline",
        });
        return fallThrough(placed.reason, { message: placed.message });
      }
      log("cell_succeeded", {
        address: args.address,
        source: "cache",
        formula: placed.formula,
      });
      ttfStats.dispatched += 1;
      return {
        outcome: "dispatched",
        dispatchedFormula: placed.formula,
        ...(placed.numberFormat
          ? { dispatchedNumberFormat: placed.numberFormat }
          : {}),
      };
    } catch (err) {
      log("cell_fell_back", {
        address: args.address,
        reason: "cache_hit_dispatch_failed",
        error: err instanceof Error ? err.message : String(err),
      });
      return fallThrough("cache_hit_dispatch_failed", {
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }
  log("cache_miss", { address: args.address });

  if (args.runCtx.attemptsRemaining !== undefined) {
    if (args.runCtx.attemptsRemaining <= 0) {
      log("cell_skipped", { address: args.address, reason: "budget_exhausted" });
      return fallThrough("budget_exhausted");
    }
    args.runCtx.attemptsRemaining -= 1;
  }

  const runTtfQuery = deps.runTtfQuery ?? defaultRunTtfQuery;
  const adapt = deps.adaptResponse ?? defaultAdaptTtfApiResponse;
  const perCallBudgetMs = deps.perCallTimeoutMs ?? EXCEL_TTF_PER_CALL_TIMEOUT_MS;
  const deadlineRemainingMs =
    args.runCtx.deadlineAt !== undefined
      ? Math.max(0, args.runCtx.deadlineAt - Date.now())
      : undefined;
  const totalBudgetMs = Math.max(
    0,
    Math.min(
      perCallBudgetMs,
      args.runCtx.budgetRemainingMs ?? perCallBudgetMs,
      deadlineRemainingMs ?? perCallBudgetMs,
    ),
  );
  if (totalBudgetMs <= 0) {
    log("cell_skipped", { address: args.address, reason: "budget_exhausted" });
    return fallThrough("budget_exhausted");
  }
  ttfStats.attempted += 1;

  // We hold both a hard-deadline timer (signal-based, kills any in-flight
  // fetch) AND pass the same budget to `runTtfQuery` (so its poll loop
  // self-terminates between fetches). When OUR timer fires the upstream
  // typically reports back as a `TtfCancelledError` (because
  // `submitTtfQuery` / `pollTtfResult` translate AbortError → cancelled),
  // not a `TtfTimeoutError` — the timeout error is only thrown by the
  // poll loop's between-fetch budget check. The flag below lets the
  // catch block tell "our timer fired" apart from "caller cancelled
  // externally" so telemetry classifies the abort honestly.
  const controller = new AbortController();
  let timedOutByDispatcher = false;
  const startedAt = Date.now();
  const abortFromCaller = (): void => controller.abort();
  args.signal?.addEventListener("abort", abortFromCaller, { once: true });
  const timer = setTimeout(() => {
    timedOutByDispatcher = true;
    controller.abort();
  }, totalBudgetMs);
  try {
    log("cell_request_started", {
      address: args.address,
      query,
      totalBudgetMs,
    });
    const { result } = await runTtfQuery(query, {
      signal: controller.signal,
      totalBudgetMs,
    });
    log("cell_response_received", {
      address: args.address,
      elapsedMs: Date.now() - startedAt,
    });
    const adapted = adapt(result);
    if (!adapted) {
      log("cell_fell_back", { address: args.address, reason: "adapter_failed" });
      return fallThrough("adapter_failed");
    }
    log("cell_response_adapted", {
      address: args.address,
      resultCount: adapted.results.length,
    });
    if (responseHasExplicitInputAnchors(adapted)) {
      log("cache_skip", { address: args.address, reason: "explicit_input_anchors" });
    } else {
      cache.set(query, adapted);
    }
    let placed: Awaited<ReturnType<typeof placeApiResponseAtCell>>;
    try {
      if (args.signal?.aborted) {
        return fallThrough("cancelled");
      }
      placed = await placeApiResponseAtCell(adapted, args, deps);
    } catch (writeErr) {
      log("cell_fell_back", {
        address: args.address,
        reason: "host_write_failed",
        error: writeErr instanceof Error ? writeErr.message : String(writeErr),
      });
      return fallThrough("host_write_failed", {
        message: writeErr instanceof Error ? writeErr.message : String(writeErr),
      });
    }
    if (placed.ok === false) {
      log("cell_fell_back", {
        address: args.address,
        reason: placed.reason,
        stage: "inline",
      });
      return fallThrough(placed.reason, { message: placed.message });
    }
    log("cell_succeeded", {
      address: args.address,
      source: "fresh",
      formula: placed.formula,
    });
    ttfStats.dispatched += 1;
    return {
      outcome: "dispatched",
      dispatchedFormula: placed.formula,
      ...(placed.numberFormat
        ? { dispatchedNumberFormat: placed.numberFormat }
        : {}),
    };
  } catch (err) {
    if (err instanceof TtfQueryDatePolicyError) {
      log("cell_fell_back", {
        address: args.address,
        reason: "invalid_query_date",
        error: err.message,
      });
      return fallThrough("invalid_query_date", { message: err.message });
    }
    if (err instanceof TtfReauthRequiredError) {
      args.runCtx.stickyDisabled = true;
      log("cell_fell_back", { address: args.address, reason: "reauth_required" });
      return fallThrough("reauth_required");
    }
    if (err instanceof TtfTimeoutError) {
      log("cell_fell_back", { address: args.address, reason: "timeout" });
      return fallThrough("timeout");
    }
    if (err instanceof TtfCancelledError || isAbortError(err)) {
      // Only "cancelled" if something other than our own deadline timer
      // aborted the call. Today the dispatcher is the only abort source,
      // so this branch effectively always classifies as "timeout"; the
      // distinction matters once a user-driven cancel signal gets
      // threaded down here (Phase 4+ hardening).
      const reason = timedOutByDispatcher ? "timeout" : "cancelled";
      log("cell_fell_back", { address: args.address, reason });
      return fallThrough(reason);
    }
    if (err instanceof TtfFailedError) {
      log("cell_fell_back", {
        address: args.address,
        reason: "upstream_failed",
        httpStatus: err.httpStatus,
        serverErrorCode: err.serverErrorCode,
      });
      return fallThrough("upstream_failed", {
        message: err.message,
        httpStatus: err.httpStatus,
        serverErrorCode: err.serverErrorCode,
      });
    }
    log("cell_fell_back", {
      address: args.address,
      reason: "unknown_error",
      error: err instanceof Error ? err.message : String(err),
    });
    return fallThrough("unknown_error", {
      message: err instanceof Error ? err.message : String(err),
    });
  } finally {
    clearTimeout(timer);
    args.signal?.removeEventListener("abort", abortFromCaller);
    if (args.runCtx.deadlineAt !== undefined) {
      args.runCtx.budgetRemainingMs = Math.max(
        0,
        args.runCtx.deadlineAt - Date.now(),
      );
    } else if (args.runCtx.budgetRemainingMs !== undefined) {
      args.runCtx.budgetRemainingMs = Math.max(
        0,
        args.runCtx.budgetRemainingMs - (Date.now() - startedAt),
      );
    }
  }
}

/**
 * Shared semaphore for outbound TTF submit+poll cycles.
 *
 * Keep this lower than the model-facing batch limit. A single
 * generate_formula call may legitimately carry many queries, but running all
 * of their polling loops at once can swamp the dev proxy and trigger
 * MaxListenersExceededWarning / Excel responsiveness issues.
 */
const TTF_MAX_IN_FLIGHT_REQUESTS = 2;
const ttfApiSemaphore = new Semaphore(TTF_MAX_IN_FLIGHT_REQUESTS);

/**
 * Execute one TTF submit+poll cycle, adapt the response, and inline the first
 * result into a single-cell formula string.
 *
 * @param query - The trimmed natural-language question for the TTF API.
 * @param signal - Optional `AbortSignal` to cancel the underlying request.
 * @param timeoutMs - Maximum wall-clock milliseconds for the submit+poll cycle.
 * @returns `{ formula: string }` on success or `{ error: string }` on failure.
 * @throws TtfTimeoutError | TtfCancelledError | TtfReauthRequiredError
 */
async function runTtfFormulaResolution(
  query: string,
  signal: AbortSignal | undefined,
  timeoutMs: number,
): Promise<{ formula: string } | { error: string }> {
  const { result } = await defaultRunTtfQuery(query, {
    signal,
    totalBudgetMs: timeoutMs,
  });
  const adapted = defaultAdaptTtfApiResponse(result);
  if (!adapted || !adapted.results || adapted.results.length === 0) {
    return { error: "TTF returned no results" };
  }
  const firstResult = adapted.results[0];
  if (firstResult.validation?.status === false) {
    return { error: "TTF returned an unvalidated formula; rephrase the query and try once." };
  }
  const inlineResult = inlineTtfFormula(firstResult);
  if (!inlineResult) {
    return { error: "Could not inline TTF response into a single-cell formula" };
  }
  const datePolicyIssue = findFqlDatePolicyIssue(inlineResult.formula);
  if (datePolicyIssue) {
    return { error: `TTF returned invalid FQL date serialization: ${datePolicyIssue.message}` };
  }
  return { formula: inlineResult.formula };
}

function looksLikeConcreteNumericDate(value: string): boolean {
  return (
    /^\d{4}[-/]\d{1,2}[-/]\d{1,2}$/.test(value.trim()) ||
    /^\d{1,2}[-/]\d{1,2}[-/]\d{4}$/.test(value.trim())
  );
}

function isDateInputKey(key: string): boolean {
  const normalized = key.toLowerCase().replace(/[^a-z0-9]/g, "");
  return (
    normalized === "date" ||
    normalized.includes("asofdate") ||
    normalized.includes("startdate") ||
    normalized.includes("enddate")
  );
}

function normalizeTtfMetadataValue(key: string, value: string): string {
  if (!isDateInputKey(key) && !looksLikeConcreteNumericDate(value)) return value;
  if (!looksLikeConcreteNumericDate(value)) return value;
  return normalizeTtfQueryAbsoluteDates(value.trim());
}

function normalizeTtfMetadataRecord(
  values: Record<string, string>,
): Record<string, string> {
  const normalized: Record<string, string> = {};
  for (const [key, value] of Object.entries(values)) {
    normalized[key] = normalizeTtfMetadataValue(key, value);
  }
  return normalized;
}

type ComparableDateInput =
  | { kind: "absolute"; value: number }
  | { kind: "serial"; value: number }
  | { kind: "relative"; unit: string; value: number };

function parseComparableDateInput(value: string): ComparableDateInput | null {
  if (looksLikeConcreteNumericDate(value)) {
    const normalized = normalizeTtfQueryAbsoluteDates(value.trim());
    const match = normalized.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) return null;
    return {
      kind: "absolute",
      value: Date.UTC(Number(match[3]), Number(match[1]) - 1, Number(match[2])),
    };
  }

  if (/^\d+(?:\.\d+)?$/.test(value.trim())) {
    return { kind: "serial", value: Number(value) };
  }

  const relative = value.trim().match(/^(-?\d+)(D|W|M|Q|Y|AY)$/i);
  if (!relative) return null;
  return {
    kind: "relative",
    unit: relative[2].toUpperCase(),
    value: Number(relative[1]),
  };
}

function isStartDateInputKey(key: string): boolean {
  const normalized = key.toLowerCase().replace(/[^a-z0-9]/g, "");
  return (
    normalized === "start" ||
    normalized.includes("startdate") ||
    normalized.includes("datestart")
  );
}

function isEndDateInputKey(key: string): boolean {
  const normalized = key.toLowerCase().replace(/[^a-z0-9]/g, "");
  return (
    normalized === "end" ||
    normalized.includes("enddate") ||
    normalized.includes("dateend")
  );
}

interface DateRangeBinding {
  startKey: string;
  endKey: string;
  start: string;
  end: string;
}

function findNamedDateRange(
  inputs: Record<string, string> | undefined,
): DateRangeBinding | null {
  if (!inputs) return null;
  let startKey: string | undefined;
  let endKey: string | undefined;
  let start: string | undefined;
  let end: string | undefined;
  for (const [key, value] of Object.entries(inputs)) {
    if (isStartDateInputKey(key)) {
      startKey = key;
      start = value;
    }
    if (isEndDateInputKey(key)) {
      endKey = key;
      end = value;
    }
  }
  if (!startKey || !endKey || start === undefined || end === undefined) return null;

  return { startKey, endKey, start, end };
}

function findReversedNamedDateRange(
  inputs: Record<string, string> | undefined,
): DateRangeBinding | null {
  const range = findNamedDateRange(inputs);
  return range && isReversedDateRange(range.start, range.end) ? range : null;
}

function isReversedDateRange(start: string, end: string): boolean {
  let parsedStart = parseComparableDateInput(start);
  let parsedEnd = parseComparableDateInput(end);
  if (
    start.trim() === "0" &&
    parsedStart?.kind === "serial" &&
    parsedEnd?.kind === "relative"
  ) {
    parsedStart = { kind: "relative", unit: parsedEnd.unit, value: 0 };
  }
  if (
    end.trim() === "0" &&
    parsedEnd?.kind === "serial" &&
    parsedStart?.kind === "relative"
  ) {
    parsedEnd = { kind: "relative", unit: parsedStart.unit, value: 0 };
  }
  if (!parsedStart || !parsedEnd || parsedStart.kind !== parsedEnd.kind) return false;
  if (
    parsedStart.kind === "relative" &&
    parsedEnd.kind === "relative" &&
    parsedStart.unit !== parsedEnd.unit
  ) {
    return false;
  }
  return parsedStart.value > parsedEnd.value;
}

function findPositionalDateRange(
  formulaTemplate: string | undefined,
  placeholders: Record<string, { value: string }> | undefined,
  commentary: string | undefined,
): DateRangeBinding | null {
  if (
    !formulaTemplate ||
    !placeholders
  ) {
    return null;
  }

  const orderedNames: string[] = [];
  const placeholderPattern = /\{([^{}]+)\}/g;
  let match: RegExpExecArray | null;
  while ((match = placeholderPattern.exec(formulaTemplate)) !== null) {
    if (!orderedNames.includes(match[1])) orderedNames.push(match[1]);
  }
  const comparableValues = orderedNames
    .map((name) => ({ key: name, value: placeholders[name]?.value }))
    .filter((entry): entry is { key: string; value: string } =>
      typeof entry.value === "string" &&
      parseComparableDateInput(entry.value) !== null,
    );
  if (comparableValues.length < 2) return null;
  const [start, end] = comparableValues;
  const hasCommentaryLabels =
    typeof commentary === "string" &&
    /\bStart Date\b/i.test(commentary) &&
    /\bEnd Date\b/i.test(commentary);
  const startKeyPattern = start.key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const endKeyPattern = end.key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const hasOrderedFunctionArguments = new RegExp(
    `\\(\\s*\\{${startKeyPattern}\\}\\s*,\\s*\\{${endKeyPattern}\\}`,
    "i",
  ).test(formulaTemplate);
  if (!hasCommentaryLabels && !hasOrderedFunctionArguments) return null;

  return {
    startKey: start.key,
    endKey: end.key,
    start: start.value,
    end: end.value,
  };
}

function findReversedPositionalDateRange(
  formulaTemplate: string | undefined,
  placeholders: Record<string, { value: string }> | undefined,
  commentary: string | undefined,
): DateRangeBinding | null {
  const range = findPositionalDateRange(
    formulaTemplate,
    placeholders,
    commentary,
  );
  return range && isReversedDateRange(range.start, range.end) ? range : null;
}

function extractOrderedDateRangeFromQuery(
  query: string,
): { start: string; end: string } | null {
  const absoluteDates =
    query.match(
      /\b(?:\d{4}[-/]\d{1,2}[-/]\d{1,2}|\d{1,2}[-/]\d{1,2}[-/]\d{4})\b/g,
    ) ?? [];
  if (absoluteDates.length === 2) {
    try {
      const [start, end] = absoluteDates.map((value) =>
        normalizeTtfQueryAbsoluteDates(value),
      );
      return isReversedDateRange(start, end) ? null : { start, end };
    } catch {
      return null;
    }
  }
  if (absoluteDates.length > 0) return null;

  const relativeDates = Array.from(
    query.matchAll(/(?:^|[\s,(])(-?\d+(?:AY|D|W|M|Q|Y))\b/gi),
    (match) => match[1].toUpperCase(),
  );
  if (relativeDates.length !== 2) return null;
  const [start, end] = relativeDates;
  return isReversedDateRange(start, end) ? null : { start, end };
}

function dateInputsAreEquivalent(left: string, right: string): boolean {
  const parsedLeft = parseComparableDateInput(left);
  const parsedRight = parseComparableDateInput(right);
  if (!parsedLeft || !parsedRight) return false;
  if (
    parsedLeft.kind === "serial" &&
    parsedLeft.value === 0 &&
    parsedRight.kind === "relative" &&
    parsedRight.value === 0
  ) {
    return true;
  }
  if (
    parsedRight.kind === "serial" &&
    parsedRight.value === 0 &&
    parsedLeft.kind === "relative" &&
    parsedLeft.value === 0
  ) {
    return true;
  }
  if (parsedLeft.kind !== parsedRight.kind) return false;
  if (
    parsedLeft.kind === "relative" &&
    parsedRight.kind === "relative" &&
    parsedLeft.unit !== parsedRight.unit
  ) {
    return false;
  }
  return parsedLeft.value === parsedRight.value;
}

function renderTtfFormulaTemplate(
  formulaTemplate: string,
  placeholders: Record<string, { value: string }>,
): string | null {
  let rendered = formulaTemplate;
  const entries = Object.entries(placeholders).sort(
    (left, right) => right[0].length - left[0].length,
  );
  for (const [key, placeholder] of entries) {
    rendered = rendered
      .split(`{${key}}`)
      .join(escapeExcelString(placeholder.value));
  }
  return /\{[^{}]+\}/.test(rendered) ? null : rendered;
}

interface RepairedTtfDateRange {
  formula: string;
  inputs: Record<string, string> | undefined;
  placeholders: Record<string, { value: string }>;
  requestedRange: { start: string; end: string };
}

function repairReversedTtfDateRange(
  query: string,
  formulaTemplate: string | undefined,
  placeholders: Record<string, { value: string }> | undefined,
  inputs: Record<string, string> | undefined,
  commentary: string | undefined,
): RepairedTtfDateRange | null {
  if (!formulaTemplate || !placeholders) return null;
  const returnedRange =
    findNamedDateRange(inputs) ??
    findPositionalDateRange(formulaTemplate, placeholders, commentary);
  if (
    !returnedRange ||
    !isReversedDateRange(returnedRange.start, returnedRange.end)
  ) {
    return null;
  }

  const requestedRange = extractOrderedDateRangeFromQuery(query);
  if (
    !requestedRange ||
    !dateInputsAreEquivalent(returnedRange.start, requestedRange.end) ||
    !dateInputsAreEquivalent(returnedRange.end, requestedRange.start)
  ) {
    return null;
  }

  const correctedPlaceholders = {
    ...placeholders,
    [returnedRange.startKey]: { value: requestedRange.start },
    [returnedRange.endKey]: { value: requestedRange.end },
  };
  const correctedFormula = renderTtfFormulaTemplate(
    formulaTemplate,
    correctedPlaceholders,
  );
  if (!correctedFormula) return null;

  const correctedInputs = inputs
    ? {
        ...inputs,
        ...(returnedRange.startKey in inputs
          ? { [returnedRange.startKey]: requestedRange.start }
          : {}),
        ...(returnedRange.endKey in inputs
          ? { [returnedRange.endKey]: requestedRange.end }
          : {}),
      }
    : undefined;

  return {
    formula: correctedFormula,
    inputs: correctedInputs,
    placeholders: correctedPlaceholders,
    requestedRange,
  };
}

/**
 * Execute one TTF submit+poll cycle and adapt the first returned result into
 * the rich tool response shape used by `generate_formula`.
 *
 * This intentionally shares the same inlining rules as the legacy
 * single-formula path (`inlineTtfFormula`) so the returned `formula` remains
 * safe to write directly into one workbook cell. Additional fields are
 * best-effort metadata for the LLM:
 *  - `formula_template` is emitted only when FactSet supplied
 *    `fql_conversion.fql_with_inputs`, because that is the only reliable
 *    reusable pattern source.
 *  - `inputs` strips host cell anchors and exposes only named input values
 *    that the model can substitute into the template.
 *
 * @param query - The trimmed natural-language question for the TTF API.
 * @param signal - Optional `AbortSignal` to cancel the underlying HTTP
 *   request mid-flight.
 * @param timeoutMs - Maximum wall-clock milliseconds for the submit+poll
 *   cycle before a `TtfTimeoutError` is thrown.
 * @returns A `TtfRichResult` on success containing the formula and optional
 *   metadata, or `{ error: string }` when the response cannot be inlined.
 * @throws TtfTimeoutError if the poll exceeds `timeoutMs`.
 * @throws TtfCancelledError if `signal` is aborted.
 * @throws TtfReauthRequiredError if FactSet returns a 401/403.
 */
async function runTtfFormulaResolutionRich(
  query: string,
  signal: AbortSignal | undefined,
  timeoutMs: number,
): Promise<TtfRichResult | { error: string }> {
  const { result, chainId } = await defaultRunTtfQuery(query, {
    signal,
    totalBudgetMs: timeoutMs,
  });
  diag("api", "ttf_raw_response", { rawResult: result });
  const adapted = defaultAdaptTtfApiResponse(result);
  if (!adapted || !adapted.results || adapted.results.length === 0) {
    return { error: "TTF returned no results" };
  }
  const firstResult = adapted.results[0];
  diag("api", "ttf_adapted", {
    formula: firstResult.formula,
    ticker: firstResult.ticker,
    fqlWithInputs: firstResult.fql_conversion?.fql_with_inputs,
    inputs: firstResult.fql_conversion?.inputs,
    validationStatus: firstResult.validation?.status,
  });
  if (firstResult.validation?.status === false) {
    return { error: "TTF returned an unvalidated formula; rephrase the query and try once." };
  }
  const inlineResult = inlineTtfFormula(firstResult);
  if (!inlineResult) {
    return { error: "Could not inline TTF response into a single-cell formula" };
  }
  let formula = inlineResult.formula;
  const { strategy, whereRefs, formulaPart } = inlineResult;

  const ticker =
    Array.isArray(firstResult.ticker) && firstResult.ticker.length === 1
      ? firstResult.ticker[0]
      : undefined;
  const fqlWithInputs = firstResult.fql_conversion?.fql_with_inputs;
  const rawInputs = firstResult.fql_conversion?.inputs;
  const description =
    (typeof (firstResult.formula_details as Record<string, unknown> | undefined)?.description === "string"
      ? ((firstResult.formula_details as Record<string, unknown>).description as string).split("\r\n")[0]
      : undefined) ||
    (typeof firstResult.formula_details?.short_description === "string"
      ? firstResult.formula_details.short_description
      : undefined);
  const commentary = firstResult.formula_conversation?.commentary;
  const validated = firstResult.validation?.status;

  let inputs: Record<string, string> | undefined;
  if (rawInputs && typeof rawInputs === "object") {
    const concreteInputs: Record<string, string> = {};
    for (const [key, entry] of Object.entries(rawInputs)) {
      if (typeof entry?.value === "string") {
        concreteInputs[key] = entry.value;
      }
    }
    if (Object.keys(concreteInputs).length > 0) {
      try {
        inputs = normalizeTtfMetadataRecord(concreteInputs);
      } catch (error) {
        if (error instanceof TtfQueryDatePolicyError) {
          return { error: error.message };
        }
        throw error;
      }
    }
  }

  // Derive formula_template and placeholders from the available structured data.
  let formula_template: string | undefined;
  let placeholders: Record<string, { value: string }> | undefined;

  if (typeof fqlWithInputs === "string" && fqlWithInputs.length > 0 && typeof ticker === "string" && ticker.length > 0) {
    // Strategy A: fql_conversion available — build a template while
    // preserving the exact FDS-family shell and leading arguments from TTF.
    formula_template = buildFactSetFormulaFromFql(
      firstResult.formula,
      '"{ticker}"',
      fqlWithInputs,
    );
    placeholders = { ticker: { value: ticker } };
    if (inputs) {
      for (const [key, value] of Object.entries(inputs)) {
        placeholders[key] = { value };
      }
    }
  } else if (strategy === "W" && whereRefs && formulaPart) {
    // Strategy W: derive template from the API Where clause.
    // Replace each cell ref in the raw formula part with {CellRef} placeholders.
    let tmpl = formulaPart;
    const sortedRefs = Object.entries(whereRefs).sort(
      (a, b) => b[0].length - a[0].length
    );
    for (const [cellRef] of sortedRefs) {
      tmpl = tmpl.split(`"&${cellRef}&"`).join(`{${cellRef}}`);
    }
    for (const [cellRef] of sortedRefs) {
      const escaped = cellRef.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const refPattern = new RegExp(`(?<!\\{)\\b${escaped}\\b`, "g");
      tmpl = tmpl.replace(refPattern, `"{${cellRef}}"`);
    }
    formula_template = tmpl;
    placeholders = {};
    let normalizedWhereRefs: Record<string, string>;
    try {
      normalizedWhereRefs = normalizeTtfMetadataRecord(whereRefs);
    } catch (error) {
      if (error instanceof TtfQueryDatePolicyError) {
        return { error: error.message };
      }
      throw error;
    }
    for (const [cellRef, value] of Object.entries(normalizedWhereRefs)) {
      placeholders[cellRef] = { value };
    }
  }

  const reversedRange =
    findReversedNamedDateRange(inputs) ??
    findReversedPositionalDateRange(
      formula_template,
      placeholders,
      commentary,
    );
  if (reversedRange) {
    const repaired = repairReversedTtfDateRange(
      query,
      formula_template,
      placeholders,
      inputs,
      commentary,
    );
    if (!repaired) {
      return {
        error:
          `TTF returned a reversed date range: Start Date ${reversedRange.start} ` +
          `is later than End Date ${reversedRange.end}. Rephrase with the earlier ` +
          `date first and try once.`,
      };
    }
    formula = repaired.formula;
    inputs = repaired.inputs;
    placeholders = repaired.placeholders;
    diag("tool", "ttf_date_range_repaired", {
      returnedStart: reversedRange.start,
      returnedEnd: reversedRange.end,
      requestedStart: repaired.requestedRange.start,
      requestedEnd: repaired.requestedRange.end,
    });
  }

  const remainingReversedRange = findReversedPositionalDateRange(
    formula_template,
    placeholders,
    commentary,
  );
  if (remainingReversedRange) {
    return {
      error:
        `TTF returned a reversed date range: Start Date ${remainingReversedRange.start} ` +
        `is later than End Date ${remainingReversedRange.end}. Rephrase with the earlier ` +
        `date first and try once.`,
    };
  }

  const rich: TtfRichResult = { formula };
  if (formula_template) rich.formula_template = formula_template;
  if (placeholders && Object.keys(placeholders).length > 0) rich.placeholders = placeholders;
  if (ticker) rich.ticker = ticker;
  if (inputs) rich.inputs = inputs;
  if (description) rich.description = description;
  if (typeof commentary === "string" && commentary.length > 0) rich.commentary = commentary;
  if (typeof validated === "boolean") rich.validated = validated;
  rich.formatHint = inferFactSetFormatHint({
    formula,
    formulaTemplate: formula_template,
    formulaDetails: firstResult.formula_details,
  });
  if (chainId) rich.chainId = chainId;

  return applyFqlDatePolicyToTtfRichResult(rich);
}

interface TtfDeadlineScope {
  signal: AbortSignal;
  deadlineAt: number;
  timedOut: () => boolean;
  remainingMs: () => number;
  cleanup: () => void;
}

/**
 * Start a per-query deadline. Callers create this only after they hold a
 * `ttfApiSemaphore` slot: with a concurrency limit of
 * {@link TTF_MAX_IN_FLIGHT_REQUESTS}, a batch's queue time would otherwise
 * consume the budget of every query behind it and starve the tail of the batch.
 */
function createTtfDeadlineScope(
  callerSignal: AbortSignal | undefined,
  timeoutMs: number,
): TtfDeadlineScope {
  const controller = new AbortController();
  const deadlineAt = Date.now() + Math.max(0, timeoutMs);
  let deadlineExpired = false;
  const abortFromCaller = (): void => controller.abort();
  if (callerSignal?.aborted) controller.abort();
  else callerSignal?.addEventListener("abort", abortFromCaller, { once: true });
  const delayMs = Math.max(0, deadlineAt - Date.now());
  const timer = setTimeout(() => {
    deadlineExpired = true;
    controller.abort();
  }, delayMs);
  return {
    signal: controller.signal,
    deadlineAt,
    timedOut: () => deadlineExpired || Date.now() >= deadlineAt,
    remainingMs: () => Math.max(0, deadlineAt - Date.now()),
    cleanup: () => {
      clearTimeout(timer);
      callerSignal?.removeEventListener("abort", abortFromCaller);
    },
  };
}

/**
 * The aggregate deadline is a dispatch gate, not a per-query budget: it stops
 * a batch from opening new TTF work once the tool call's wall clock is gone,
 * while queries that already hold a semaphore slot keep their own full budget.
 */
function aggregateDeadlineExhausted(aggregateDeadlineAt?: number): boolean {
  return aggregateDeadlineAt !== undefined && Date.now() >= aggregateDeadlineAt;
}

function waitForPromiseOrAbort<T>(
  promise: Promise<T>,
  signal: AbortSignal,
): Promise<T> {
  if (signal.aborted) {
    return Promise.reject(new TtfCancelledError("TTF request was cancelled"));
  }
  return new Promise<T>((resolve, reject) => {
    const onAbort = (): void => {
      reject(new TtfCancelledError("TTF request was cancelled"));
    };
    signal.addEventListener("abort", onAbort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener("abort", onAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener("abort", onAbort);
        reject(error);
      },
    );
  });
}

/**
 * Resolve a natural-language query to a FactSet formula string via TTF,
 * without writing to any cell. Intended for use as an LLM tool executor.
 *
 * @param query - The natural-language question to submit to the TTF API.
 * @param signal - Optional `AbortSignal` for cooperative cancellation.
 * @param timeoutMs - Per-call timeout in milliseconds. Defaults to
 *   `EXCEL_TTF_PER_CALL_TIMEOUT_MS` when omitted.
 * @returns `{ formula: string }` on success or `{ error: string }` on any failure.
 */
export async function resolveFormulaViaTtf(
  query: string,
  signal?: AbortSignal,
  timeoutMs?: number,
  aggregateDeadlineAt?: number,
): Promise<{ formula: string } | { error: string }> {
  const trimmed = query.trim();
  if (!trimmed) {
    return { error: "Empty query" };
  }
  if (signal?.aborted) return { error: "TTF request was cancelled" };
  const effectiveTimeoutMs = timeoutMs ?? EXCEL_TTF_PER_CALL_TIMEOUT_MS;
  if (aggregateDeadlineExhausted(aggregateDeadlineAt)) {
    return { error: "TTF aggregate deadline exhausted before dispatch" };
  }
  try {
    return await ttfApiSemaphore.run(async () => {
      if (aggregateDeadlineExhausted(aggregateDeadlineAt)) {
        return { error: "TTF aggregate deadline exhausted while queued" };
      }
      const scope = createTtfDeadlineScope(signal, effectiveTimeoutMs);
      try {
        const resolved = await runTtfFormulaResolution(
          trimmed,
          scope.signal,
          scope.remainingMs(),
        );
        return resolved;
      } catch (err) {
        if (signal?.aborted) {
          return { error: "TTF request was cancelled" };
        }
        if (scope.timedOut()) {
          return { error: "TTF request timed out" };
        }
        if (err instanceof TtfTimeoutError) {
          return { error: "TTF request timed out" };
        }
        if (err instanceof TtfCancelledError) {
          return { error: "TTF request was cancelled" };
        }
        if (err instanceof TtfReauthRequiredError) {
          try {
            await waitForPromiseOrAbort(
              forceReconnectFactSetOAuth(),
              scope.signal,
            );
          } catch {
            if (signal?.aborted) {
              return { error: "TTF request was cancelled" };
            }
            if (scope.timedOut()) {
              return { error: "TTF request timed out during authentication" };
            }
            return { error: "FactSet authentication expired — reconnect was dismissed or failed" };
          }
          try {
            if (scope.remainingMs() <= 0) {
              return { error: "TTF request timed out before authentication retry" };
            }
            const resolved = await runTtfFormulaResolution(
              trimmed,
              scope.signal,
              scope.remainingMs(),
            );
            return resolved;
          } catch (retryErr) {
            if (scope.timedOut()) {
              return { error: "TTF request timed out" };
            }
            return { error: retryErr instanceof Error ? retryErr.message : String(retryErr) };
          }
        }
        return { error: err instanceof Error ? err.message : String(err) };
      } finally {
        scope.cleanup();
      }
    }, signal);
  } catch (error) {
    if (signal?.aborted || isAbortError(error)) {
      return { error: "TTF request was cancelled" };
    }
    return { error: error instanceof Error ? error.message : String(error) };
  }
}

/**
 * Structured metadata returned by `inlineTtfFormula` alongside the
 * inlined formula string.
 */
interface InlineResult {
  formula: string;
  strategy: "A" | "W" | "B" | "C";
  whereRefs?: Record<string, string>;
  formulaPart?: string;
}

/**
 * Rich `generate_formula` result.
 *
 * `formula` is the exact, inlined formula when it passes local policy.
 * It may be omitted when its concrete date literal is unsafe but a valid
 * `formula_template` remains available for workbook placeholder binding.
 * At least one of `formula` / `formula_template` is present on success.
 */
export interface TtfRichResult {
  /** Ready-to-use inlined FactSet formula, e.g. `=FDS("AAPL-US","...")`. */
  formula?: string;
  /** Reusable template with `{CellRef}` placeholders derived from API Where clause or fql_conversion. */
  formula_template?: string;
  /**
   * Map of API placeholders to their values. Each key is a cell reference
   * from the API's Where clause (e.g. "A1", "A2") or fql_conversion input name.
   */
  placeholders?: Record<string, { value: string }>;
  /** Single resolved FactSet identifier, when the response names exactly one. */
  ticker?: string;
  /** TTF named inputs mapped to literal values, excluding host anchor cells. */
  inputs?: Record<string, string>;
  /** Human-readable FactSet formula description (metric name and category). */
  description?: string;
  /** Rich explanation of the formula configuration and parameter choices. */
  commentary?: string;
  /** FactSet validation status, when present on the response. */
  validated?: boolean;
  /**
   * Output-scale metadata used to choose an Excel number format without
   * changing the provider-returned formula or value.
   */
  formatHint?: FactSetFormatHint;
  /**
   * Upstream FactSet TTF chain id for this resolution. Internal correlation
   * only: the tool layer lifts it onto the Langfuse observation metadata and
   * strips it from the model-facing output, so the model never sees it.
   */
  chainId?: string;
}

/**
 * Keep every independently safe representation from a rich TTF response.
 *
 * Workbook flows use `formula_template` as their source of truth, while
 * chat/standalone flows may use the inlined `formula`. A date-policy failure
 * in one representation must not discard the other when it is valid.
 */
export function applyFqlDatePolicyToTtfRichResult(
  rich: TtfRichResult,
): TtfRichResult | { error: string } {
  const safe: TtfRichResult = { ...rich };
  const formulaIssue =
    typeof safe.formula === "string"
      ? findFqlDatePolicyIssue(safe.formula)
      : null;
  const templateIssue =
    typeof safe.formula_template === "string"
      ? findFqlDatePolicyIssue(safe.formula_template)
      : null;

  if (formulaIssue) {
    delete safe.formula;
  }
  if (templateIssue) {
    delete safe.formula_template;
    delete safe.placeholders;
  }

  if (!safe.formula && !safe.formula_template) {
    const issue = formulaIssue ?? templateIssue;
    return {
      error: issue
        ? `TTF returned invalid FQL date serialization: ${issue.message}`
        : "TTF returned no usable formula or formula template.",
    };
  }

  return safe;
}

/**
 * Like `resolveFormulaViaTtf` but returns structured metadata alongside the
 * inlined formula so the LLM can reuse the pattern across tickers/periods and
 * explain what the formula returns in chat.
 *
 * Errors intentionally mirror `resolveFormulaViaTtf` so callers can swap to
 * this richer surface without changing auth, timeout, or retry handling.
 *
 * @param query - The natural-language question containing company identifier,
 *   metric, and time period (e.g. "AAPL-US revenue for most recent quarter").
 * @param signal - Optional `AbortSignal` for cooperative cancellation. When
 *   aborted, resolves to `{ error: "TTF request was cancelled" }`.
 * @param timeoutMs - Per-call timeout in milliseconds. Defaults to
 *   `EXCEL_TTF_PER_CALL_TIMEOUT_MS` when omitted.
 * @returns A `TtfRichResult` on success or `{ error: string }` on any failure
 *   (timeout, cancellation, empty query, auth expiry, inline failure).
 */
export async function resolveFormulaViaTtfRich(
  query: string,
  signal?: AbortSignal,
  timeoutMs?: number,
  aggregateDeadlineAt?: number,
): Promise<TtfRichResult | { error: string }> {
  const trimmed = query.trim();
  if (!trimmed) {
    return { error: "Empty query" };
  }
  if (signal?.aborted) return { error: "TTF request was cancelled" };
  const effectiveTimeoutMs = timeoutMs ?? EXCEL_TTF_PER_CALL_TIMEOUT_MS;
  if (aggregateDeadlineExhausted(aggregateDeadlineAt)) {
    return { error: "TTF aggregate deadline exhausted before dispatch" };
  }
  try {
    return await ttfApiSemaphore.run(async () => {
      if (aggregateDeadlineExhausted(aggregateDeadlineAt)) {
        return { error: "TTF aggregate deadline exhausted while queued" };
      }
      const scope = createTtfDeadlineScope(signal, effectiveTimeoutMs);
      try {
        return await runTtfFormulaResolutionRich(
          trimmed,
          scope.signal,
          scope.remainingMs(),
        );
      } catch (err) {
        if (signal?.aborted) {
          return { error: "TTF request was cancelled" };
        }
        if (scope.timedOut()) {
          return { error: "TTF request timed out" };
        }
        if (err instanceof TtfTimeoutError) {
          return { error: "TTF request timed out" };
        }
        if (err instanceof TtfCancelledError) {
          return { error: "TTF request was cancelled" };
        }
        if (err instanceof TtfReauthRequiredError) {
          try {
            await waitForPromiseOrAbort(
              forceReconnectFactSetOAuth(),
              scope.signal,
            );
          } catch {
            if (signal?.aborted) {
              return { error: "TTF request was cancelled" };
            }
            if (scope.timedOut()) {
              return { error: "TTF request timed out during authentication" };
            }
            return { error: "FactSet authentication expired — reconnect was dismissed or failed" };
          }
          try {
            if (scope.remainingMs() <= 0) {
              return { error: "TTF request timed out before authentication retry" };
            }
            return await runTtfFormulaResolutionRich(
              trimmed,
              scope.signal,
              scope.remainingMs(),
            );
          } catch (retryErr) {
            if (scope.timedOut()) {
              return { error: "TTF request timed out" };
            }
            return { error: retryErr instanceof Error ? retryErr.message : String(retryErr) };
          }
        }
        if (err instanceof TtfFailedError) {
          return { error: err.message };
        }
        return { error: err instanceof Error ? err.message : String(err) };
      } finally {
        scope.cleanup();
      }
    }, signal);
  } catch (error) {
    if (signal?.aborted || isAbortError(error)) {
      return { error: "TTF request was cancelled" };
    }
    return { error: error instanceof Error ? error.message : String(error) };
  }
}

/** One entry in a batched TTF resolution, aligned 1:1 (and in order) with the input queries. */
/**
 * One batch result, index-aligned with the input queries. Success entries
 * spread the {@link TtfRichResult} fields at the top level (mirroring the
 * single-query response so the model reads `formula` / `formula_template` /
 * `placeholders` the same way in both modes) alongside the `query`
 * discriminator. Failure entries carry `query` + `error`.
 */
export type TtfBatchEntry =
  | ({ query: string } & TtfRichResult)
  | { query: string; error: string };

/**
 * Resolve several natural-language queries to FactSet formulas in one batch.
 *
 * Semantics (all required by the latency plan):
 *  - **Bounded**: at most `TTF_MAX_CONCURRENT_QUERIES` are dispatched; the
 *    overflow is returned as per-entry errors so the caller can continue the
 *    remainder in another batch.
 *  - **Single wave**: accepted queries are dispatched together rather than in
 *    sequential rounds, so a batch costs roughly one round trip's latency.
 *  - **Order-preserving**: the returned array is index-aligned with `queries`.
 *  - **Deduped**: identical (trimmed) queries are resolved once and fanned back
 *    out to every position that requested them.
 *  - **Concurrency-capped**: dispatch goes through `resolveFormulaViaTtfRich`,
 *    which acquires the shared, lower in-flight request semaphore. The batch
 *    may accept more queries than are sent over the network at once.
 *  - **Per-query errors**: one failing query never fails the whole batch.
 *  - **Cancellation-aware**: when `signal` is aborted, in-flight queries reject
 *    and not-yet-started ones short-circuit to a cancelled error.
 *
 * @param queries - Natural-language queries (company ID, metric, period each).
 * @param signal - Optional `AbortSignal` for cooperative cancellation.
 * @param timeoutMs - Per-query timeout; defaults to `EXCEL_TTF_PER_CALL_TIMEOUT_MS`.
 * @returns One `TtfBatchEntry` per input query, in input order.
 */
export async function resolveFormulasViaTtfRichBatch(
  queries: readonly string[],
  signal?: AbortSignal,
  timeoutMs?: number,
  aggregateDeadlineAt?: number,
): Promise<TtfBatchEntry[]> {
  const normalized = queries.map((q) => (typeof q === "string" ? q.trim() : ""));
  const overflow = normalized.length > TTF_MAX_CONCURRENT_QUERIES;

  // Resolve each unique, accepted, non-empty query exactly once. The whole
  // wave is dispatched together: `ttfApiSemaphore` throttles what actually
  // reaches the network, and each query's own timeout starts only once it
  // holds a slot, so waiting in the queue never costs a query its budget.
  const uniqueQueries = Array.from(
    new Set(
      normalized
        .slice(0, TTF_MAX_CONCURRENT_QUERIES)
        .filter((q) => q.length > 0),
    ),
  );

  const resultByQuery = new Map<string, TtfRichResult | { error: string }>();
  await Promise.all(
    uniqueQueries.map(async (query) => {
      try {
        resultByQuery.set(
          query,
          await resolveFormulaViaTtfRich(
            query,
            signal,
            timeoutMs,
            aggregateDeadlineAt,
          ),
        );
      } catch (error) {
        resultByQuery.set(query, {
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }),
  );

  return normalized.map((q, index): TtfBatchEntry => {
    if (q.length === 0) {
      return { query: q, error: "Empty query" };
    }
    if (overflow && index >= TTF_MAX_CONCURRENT_QUERIES) {
      return {
        query: q,
        error:
          `Batch exceeded the ${TTF_MAX_CONCURRENT_QUERIES}-query limit; ` +
          `resubmit this query in another generate_formula call.`,
      };
    }
    const res = resultByQuery.get(q);
    if (!res) {
      return { query: q, error: "TTF request was cancelled" };
    }
    if ("error" in res) {
      return { query: q, error: res.error };
    }
    // Spread the rich fields to top level so a batch entry reads exactly like
    // the single-query response (plus the `query` discriminator).
    return { query: q, ...res };
  });
}
